import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import type { User } from '../api/types';

interface AuthCtx {
  user: User | null;
  login: (phone: string, name?: string) => Promise<User>;
  logout: () => void;
  refresh: (updated: User) => void;
  isAdmin: boolean;
}

const Ctx = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [user, setUser] = useState<User | null>(() => {
    const raw = localStorage.getItem('bolao_user');
    return raw ? JSON.parse(raw) : null;
  });

  const login = useCallback(
    async (phone: string, name?: string) => {
      const prevRaw = localStorage.getItem('bolao_user');
      const prevId = prevRaw ? JSON.parse(prevRaw).id : null;

      const { data } = await api.post('/auth/login', { phone, name });
      localStorage.setItem('bolao_token', data.token);
      localStorage.setItem('bolao_user', JSON.stringify(data.user));

      if (prevId !== data.user.id) qc.clear();
      setUser(data.user);
      return data.user as User;
    },
    [qc],
  );

  const logout = useCallback(() => {
    localStorage.removeItem('bolao_token');
    localStorage.removeItem('bolao_user');
    qc.clear();
    setUser(null);
  }, [qc]);

  const refresh = useCallback((updated: User) => {
    localStorage.setItem('bolao_user', JSON.stringify(updated));
    setUser(updated);
  }, []);

  return (
    <Ctx.Provider value={{ user, login, logout, refresh, isAdmin: !!user?.is_admin }}>
      {children}
    </Ctx.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useAuth fora do AuthProvider');
  return ctx;
}
